import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Bot, KeyRound, Loader2, Plug } from "lucide-react";
import { fetchAPI, formatDate } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const WINDOWS = [7, 30, 90];
const SHORT_SCOPE = {
	"programs:read": "lettura",
	"programs:write": "programmi",
	"programs:modify": "cancellare",
	"competitions:write": "gare",
	"models:write": "modelli",
};

const number = (value) => Number(value || 0).toLocaleString("it-IT");

function Stat({ label, value, warn = false }) {
	return (
		<div
			className={`rounded-sm border p-3 ${
				warn && value > 0 ? "border-amber-500/40 bg-amber-500/10" : "border-border bg-secondary/40"
			}`}
		>
			<p className="font-heading text-2xl tabular-nums">{value}</p>
			<p className="text-xs text-muted-foreground">{label}</p>
		</div>
	);
}

/**
 * Chi usa l'assistente con la propria chiave API e chi i client MCP, e quanto.
 *
 * Sta in «Gestione Coach». I dati vengono da `GET /ai-agent/admin/usage`
 * (services/ai_usage.py): della chiave solo il suggerimento mascherato,
 * dell'uso solo contatori giornalieri, conservati 90 giorni. Lo storico parte
 * dal 27 ottobre 2026: prima non si registrava niente.
 */
export function AiUsageAdminTab() {
	const [days, setDays] = useState(30);
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			setData(await fetchAPI(`/ai-agent/admin/usage?days=${days}`));
		} catch (err) {
			setError(err?.message || "Errore");
		} finally {
			setLoading(false);
		}
	}, [days]);

	useEffect(() => {
		load();
	}, [load]);

	const totals = data?.totals || {};
	const coaches = data?.coaches || [];

	return (
		<Card className="bg-card border-border rounded-sm" data-testid="ai-usage-admin">
			<CardHeader className="space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-2">
					<CardTitle className="font-heading text-xl flex items-center gap-2">
						<Bot className="w-5 h-5 text-volt" />
						Uso dell'AI
					</CardTitle>
					<div className="flex gap-1">
						{WINDOWS.map((value) => (
							<Button
								key={value}
								size="sm"
								variant={days === value ? "default" : "outline"}
								className={`h-7 rounded-sm text-xs ${days === value ? "bg-volt hover:bg-volt/90" : ""}`}
								onClick={() => setDays(value)}
							>
								{value} giorni
							</Button>
						))}
					</div>
				</div>
				<p className="text-xs text-muted-foreground">
					Chiavi API dei coach (solo il suggerimento, mai la chiave), collegamenti MCP
					attivi e uso. Si registrano solo contatori giornalieri, conservati{" "}
					{data?.retention_days || 90} giorni; lo storico parte dal 27 ottobre 2026.
				</p>
				<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
					<Stat label="Con chiave API" value={totals.with_api_key ?? 0} />
					<Stat label="Con un client MCP" value={totals.with_mcp ?? 0} />
					<Stat label={`Attivi in ${days} giorni`} value={totals.active ?? 0} />
					<Stat
						label="MCP senza permesso modelli"
						value={totals.mcp_missing_models_write ?? 0}
						warn
					/>
				</div>
			</CardHeader>
			<CardContent>
				{loading ? (
					<div className="flex items-center justify-center py-8 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin" />
					</div>
				) : error ? (
					<p className="text-sm text-blaze">{error}</p>
				) : coaches.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						Nessun coach usa ancora l'assistente o un client MCP.
					</p>
				) : (
					<div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
						{coaches.map((coach) => (
							<div
								key={coach.user_id}
								className="space-y-2 rounded-sm border border-border p-3"
								data-testid={`ai-usage-${coach.user_id}`}
							>
								<div className="min-w-0">
									<p className="truncate font-medium">{coach.name}</p>
									{coach.email && (
										<p className="truncate text-xs text-muted-foreground">{coach.email}</p>
									)}
								</div>

								<div className="flex items-start gap-2 text-xs">
									<KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
									{coach.api_key ? (
										<div className="min-w-0">
											<p>
												{coach.api_key.provider} · {coach.api_key.api_key_hint}
												{coach.api_key.created_at &&
													` · dal ${formatDate(coach.api_key.created_at)}`}
											</p>
											<p className="text-muted-foreground">
												{number(coach.usage.assistant_turns)} messaggi ·{" "}
												{number(coach.usage.input_tokens)} token in ·{" "}
												{number(coach.usage.output_tokens)} out ·{" "}
												{number(coach.usage.assistant_writes)} scritture
												{coach.last_assistant_at
													? ` · ultimo ${formatDate(coach.last_assistant_at)}`
													: " · mai usato nel periodo"}
											</p>
										</div>
									) : (
										<p className="text-muted-foreground">Nessuna chiave API</p>
									)}
								</div>

								<div className="flex items-start gap-2 text-xs">
									<Plug className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
									{coach.mcp.length > 0 ? (
										<div className="min-w-0 space-y-1">
											{coach.mcp.map((connection) => (
												<div key={connection.client_id}>
													<p className="flex flex-wrap items-center gap-1">
														<span>{connection.client_name}</span>
														<span className="text-muted-foreground">
															· {connection.scopes.map((s) => SHORT_SCOPE[s] || s).join(", ")}
														</span>
														{connection.missing_scopes.includes("models:write") && (
															<Badge className="h-5 rounded-sm border-0 bg-amber-500/20 px-1.5 text-[10px] text-amber-300">
																<AlertTriangle className="mr-1 h-3 w-3" />
																senza modelli
															</Badge>
														)}
														{/* Il client usa la fotografia di un elenco di strumenti
														    vecchio (o di cui non c'e' traccia): lavora coi pezzi
														    che aveva al collegamento. */}
														{connection.catalog &&
															connection.catalog.status !== "current" && (
																<Badge className="h-5 rounded-sm border-0 bg-amber-500/20 px-1.5 text-[10px] text-amber-300">
																	<AlertTriangle className="mr-1 h-3 w-3" />
																	{connection.catalog.status === "stale"
																		? `strumenti vecchi (+${connection.catalog.new_tools})`
																		: "strumenti da verificare"}
																</Badge>
															)}
													</p>
													<p className="text-muted-foreground">
														{connection.connected_at &&
															`collegato ${formatDate(connection.connected_at)}`}
														{connection.last_used_at
															? ` · ultimo uso ${formatDate(connection.last_used_at)}`
															: " · nessun uso nel periodo"}
													</p>
												</div>
											))}
											<p className="text-muted-foreground">
												{number(coach.usage.mcp_calls)} chiamate ·{" "}
												{number(coach.usage.mcp_writes)} scritture nel periodo
											</p>
										</div>
									) : (
										<p className="text-muted-foreground">Nessun client MCP</p>
									)}
								</div>

								{coach.usage.active_days > 0 && (
									<p className="text-[11px] text-muted-foreground">
										Attivo {coach.usage.active_days}{" "}
										{coach.usage.active_days === 1 ? "giorno" : "giorni"} su {days}
									</p>
								)}
							</div>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
